import type { ResolvedChartSegmentDefault } from '@digital-twins/shared-types'

export interface SlideTrace {
  slideId?: string
  questionId?: string
  metricId?: string
  assetId?: string
  questionIds: Array<string>
  metricIds: Array<string>
  chartSegmentDefault?: ResolvedChartSegmentDefault
}

const PART_SUFFIX = /__part_\d+$/

function asStringArray(value: unknown): Array<string> | undefined {
  if (!Array.isArray(value)) {
    return undefined
  }
  const items = value.filter((item): item is string => typeof item === 'string' && item.length > 0)
  return items.length > 0 ? items : undefined
}

export function inferQuestionIdFromSlideId(slideId: string | undefined): string | undefined {
  if (!slideId) {
    return undefined
  }
  const base = slideId.replace(PART_SUFFIX, '')
  if (!base.startsWith('slide_')) {
    return undefined
  }
  const stem = base.slice('slide_'.length)
  return stem ? `q_${stem}` : undefined
}

function metricIdsFromMetrics(slide: Record<string, unknown>): Array<string> | undefined {
  const metrics = slide.metrics
  if (!Array.isArray(metrics)) {
    return undefined
  }
  const ids = metrics
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return ''
      }
      const metricId = (item as Record<string, unknown>).metricId
      return typeof metricId === 'string' ? metricId : ''
    })
    .filter((id) => id.length > 0)
  return ids.length > 0 ? ids : undefined
}

export function readSlideTrace(slide: Record<string, unknown>): SlideTrace {
  const trace = slide.trace && typeof slide.trace === 'object'
    ? (slide.trace as Record<string, unknown>)
    : {}
  const questionIds =
    asStringArray(slide.questionIds) ?? asStringArray(trace.questionIds) ?? []
  const metricIds =
    asStringArray(slide.metricIds) ??
    asStringArray(trace.metricIds) ??
    metricIdsFromMetrics(slide) ??
    []
  const slideId = typeof slide.id === 'string' ? slide.id : undefined
  const inferredQuestionId = inferQuestionIdFromSlideId(slideId)
  const questionId = questionIds[0] ?? inferredQuestionId
  const slideAssetId = typeof slide.assetId === 'string' && slide.assetId.length > 0
    ? slide.assetId
    : undefined
  const traceAssetId = typeof trace.assetId === 'string' && trace.assetId.length > 0
    ? trace.assetId
    : asStringArray(trace.assetId)?.[0]
  const assetId = slideAssetId ?? traceAssetId
  // The deck schema validates this closed drawable payload. Keep the recorded
  // source separate from the current metadata used by manual gear selections.
  const chartSegmentDefault = trace.chartSegmentDefault as ResolvedChartSegmentDefault | null | undefined
  if (chartSegmentDefault && chartSegmentDefault.slideId !== slideId) {
    throw new Error('Recorded chart source belongs to a different slide.')
  }
  return {
    slideId,
    questionId,
    metricId: metricIds[0],
    assetId,
    questionIds: questionIds.length > 0 ? questionIds : questionId ? [questionId] : [],
    metricIds,
    ...(chartSegmentDefault ? { chartSegmentDefault } : {}),
  }
}

export function hasChartSegmentTrace(slide: Record<string, unknown>): boolean {
  const trace = readSlideTrace(slide)
  return Boolean(trace.slideId)
}

const OPEN_END_THEME_CANONICAL_SLIDE_TYPES = new Set([
  'qual_descriptive_themes',
  'qual_positive_themes',
  'qual_negative_themes',
])

export function isOpenEndSegmentBlocked(
  slide: Record<string, unknown>,
  blockedIds: Array<string> | undefined,
): boolean {
  if (!blockedIds?.length) {
    return false
  }
  const blocked = new Set(blockedIds)
  const trace = readSlideTrace(slide)
  // Question ids stay in meta for API refusal messaging; only slide ids hide the
  // gear so a mixed quant + open-end slide keeps segmenting for its metrics.
  if (trace.slideId && blocked.has(trace.slideId)) {
    return true
  }
  return false
}

/** Open-end theme slides ship as choice-bar shapes but have no quantitative segment metric. */
export function shouldHideChartSegmentGear(
  slide: Record<string, unknown>,
  openEndSegmentBlockedIds?: Array<string>,
): boolean {
  if (isOpenEndSegmentBlocked(slide, openEndSegmentBlockedIds)) {
    return true
  }
  const canonicalSlideType = slide.canonicalSlideType
  if (
    typeof canonicalSlideType === 'string'
    && OPEN_END_THEME_CANONICAL_SLIDE_TYPES.has(canonicalSlideType)
  ) {
    return true
  }
  const trace = slide.trace && typeof slide.trace === 'object'
    ? (slide.trace as Record<string, unknown>)
    : {}
  const qualitativeAnalysisIds =
    asStringArray(slide.qualitativeAnalysisIds) ?? asStringArray(trace.qualitativeAnalysisIds) ?? []
  if (qualitativeAnalysisIds.length === 0) {
    return false
  }
  const metricIds =
    asStringArray(slide.metricIds) ??
    asStringArray(trace.metricIds) ??
    metricIdsFromMetrics(slide) ??
    []
  return metricIds.length === 0
}
